import { Injectable, EventEmitter } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpService } from 'src/app/services/http.service';

interface OnlyDates {
  bussinessHours: BussinessHours[];
  onlyDate: String;
  events: Events[];
}

interface Events {
  endTime: String;
  startTime: String;
}

interface BussinessHours {
  daysOfWeek: String[];
  endTime: String;
  startTime: String;
  available: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class CalendarService {
  public months = [
    {
      month: 'ENERO',
      value: 0,
    },
    {
      month: 'FEBRERO',
      value: 1,
    },
    {
      month: 'MARZO',
      value: 2,
    },
    {
      month: 'ABRIL',
      value: 3,
    },
    {
      month: 'MAYO',
      value: 4,
    },
    {
      month: 'JUNIO',
      value: 5,
    },
    {
      month: 'JULIO',
      value: 6,
    },
    {
      month: 'AGOSTO',
      value: 7,
    },
    {
      month: 'SEPTIEMBRE',
      value: 8,
    },
    {
      month: 'OCTUBRE',
      value: 9,
    },
    {
      month: 'NOVIEMBRE',
      value: 10,
    },
    {
      month: 'DICIEMBRE',
      value: 11,
    },
  ];

  public days = [
    {
      day: 'LUNES',
      value: 1,
    },
    {
      day: 'MARTES',
      value: 2,
    },
    {
      day: 'MIERCOLES',
      value: 3,
    },
    {
      day: 'JUEVES',
      value: 4,
    },
    {
      day: 'VIERNES',
      value: 5,
    },
    {
      day: 'SABADO',
      value: 6,
    },
    {
      day: 'DOMINGO',
      value: 0,
    },
  ];

  public emitNewDate = new EventEmitter();

  constructor(private httpSv: HttpService, private trSv: TranslateService) {}

  convertData() {
    return new Promise<any[]>(async (resolve) => {
      const schedule = await this.httpSv.itemIndex('attentionSchedule');
      of(schedule)
        .pipe(
          map((scheduleFilter) => {
            const item: any[] = scheduleFilter.data.item;
            item.forEach((data) => {
              data.day = data.day.split(',');
              data.day = data.day.map((item) => Number(item));
              data.day = data.day.map((item) => {
                const day = this.days.find((day) => day.value === item);
                item = {
                  day: day.day,
                  item,
                };
                return item;
              });
            });
            return item;
          })
        )
        .subscribe((data) => {
          resolve(data);
        });
    });
  }

  compareSchedule(date, schedules) {
    const onDate = new Date(date);
    const fixedHour = date.split('T')[1];
    let canStore = false;
    for (const item of schedules) {
      const days: any[] = item.day.split(',');
      const storeDay = onDate.getDay();
      const isInSchedule = days.find((item) => Number(item) === storeDay);
      if (isInSchedule) {
        if (fixedHour >= item.init && fixedHour <= item.end) {
          canStore = true;
          break;
        }
      } else {
        canStore = false;
      }
    }
    return canStore;
  }

  checkIfADayIsAvailable(
    events: any[],
    bussinessHours: any[],
    duration: number
  ) {
    const durationToMs = duration * 60000;
    const deepCopyArr: any[] = JSON.parse(JSON.stringify(events));
    const onlyDates: OnlyDates[] = [];
    deepCopyArr
      .map((item) => {
        item.startTime = item.start.split('T')[1];
        item.endTime = item.end.split('T')[1];
        return item;
      })
      .sort((a, b) => {
        // ORDENO DESDE LA PRIMER FECHA AGENDADA HASTA LA ULTIMA
        // DE ESTA MANERA PUEDO SABER LOS INTERVALOS QUE EXISTEN ENTRE UNO Y OTRO
        const aTime = this.timeToMs(a);
        const bTime = this.timeToMs(b);
        return aTime.initToMs - bTime.initToMs;
      })
      .forEach((item) => {
        const onlyDate = item.start.split('T')[0];
        const day = new Date(item.start).getDay();
        const findDayOfBussiness = bussinessHours.filter((hours) => {
          hours.available = false;
          return !!hours.daysOfWeek.includes(day.toString());
        });
        if (!onlyDates.find((item) => item.onlyDate === onlyDate)) {
          onlyDates.push({
            onlyDate,
            events: [item],
            bussinessHours: JSON.parse(JSON.stringify(findDayOfBussiness)),
          });
        } else {
          const retrieveDate = onlyDates.find(
            (item) => item.onlyDate === onlyDate
          );
          retrieveDate.events.push(item);
        }
      });
    for (const bussiness of onlyDates) {
      for (let index = 0; index < bussiness.events.length; index++) {
        const currentEvent = bussiness.events[index];

        const nextEvent = bussiness.events[index + 1];
        const { initToMs, endTimeToMs } = this.timeToMs(currentEvent);
        const correctBussinessHour = bussiness.bussinessHours.find((x) => {
          const bussinessHour = this.timeToMs(x);
          if (
            initToMs >= bussinessHour.initToMs &&
            endTimeToMs <= bussinessHour.endTimeToMs
          ) {
            return true;
          }

          if (
            bussinessHour.initToMs >= initToMs &&
            endTimeToMs <= bussinessHour.endTimeToMs
          ) {
            return true;
          }
        });
        if (correctBussinessHour) {
          if (index === 0) {
            //SI EL INDEX ES 0 ENTONCES COMPARO ES CON LA HORA DE INICIO DEL HORARIO
            const bussinessHour = this.timeToMs(correctBussinessHour);
            if (initToMs - bussinessHour.initToMs >= durationToMs) {
              //MARCAR COMO DISPONIBLE
              correctBussinessHour.available = true;
            }

            if (nextEvent) {
              // COMPARO LA FECHA DE FIN DEL SERVICIO CON LA FECHA DE INICIO DEL
              // SIGUIENTE SERVICIO, PARA VER SI HAY CUPO PARA UNA SIGUIENTE AGENDA

              const bussinessHourNextEv = this.timeToMs(nextEvent);
              if (
                bussinessHourNextEv.initToMs >= bussinessHour.initToMs &&
                bussinessHourNextEv.endTimeToMs <= bussinessHour.endTimeToMs
              ) {
                const diff = bussinessHourNextEv.initToMs - endTimeToMs;
                if (diff >= durationToMs) {
                  //MARCAR COMO DISPONIBLE
                  correctBussinessHour.available = true;
                }
              }
            } else {
              // COMPARO LA FECHA DE FIN DEL SERVICIO CON LA FECHA DE FIN DEL HORARIO
              // PARA VER SI HAY CUPO PARA UNA SIGUIENTE AGENDA
              if (bussinessHour.endTimeToMs - endTimeToMs >= durationToMs) {
                // MARCAR COMO DISPONIBLE
                correctBussinessHour.available = true;
              }
            }
          } else {
            const { initToMs, endTimeToMs } = this.timeToMs(currentEvent);
            const bussinessHourSchedule = this.timeToMs(correctBussinessHour);
            const bussinessHour = nextEvent ? this.timeToMs(nextEvent) : false;
            if (
              nextEvent &&
              bussinessHour &&
              bussinessHour.initToMs >= bussinessHourSchedule.initToMs &&
              bussinessHour.endTimeToMs <= bussinessHourSchedule.endTimeToMs
            ) {
              if (bussinessHour.initToMs - endTimeToMs >= durationToMs) {
                //MARCAR COMO DISPONIBLE
                correctBussinessHour.available = true;
              }
            } else if (
              initToMs >= bussinessHourSchedule.initToMs &&
              endTimeToMs <= bussinessHourSchedule.endTimeToMs
            ) {
              const bussinessHour = this.timeToMs(correctBussinessHour);
              if (bussinessHour.endTimeToMs - endTimeToMs >= durationToMs) {
                //MARCAR COMO DISPONIBLE
                correctBussinessHour.available = true;
              }
            }
          }
        }
      }
    }
    const unavailableDates = onlyDates.filter((date) => {
      return !date.bussinessHours.find(
        (bussinessHour) => bussinessHour.available
      );
    });
    unavailableDates.forEach((date) => {
      const tdEl = document.querySelector(`[data-date="${date.onlyDate}"]`);
      const div = document.createElement('DIV');
      const p = document.createElement('P');
      p.setAttribute('data-allreserved', 'true');
      p.innerText = this.trSv.instant('TODO_RESERVADO');
      div.appendChild(p);
      div.classList.add('all-reserved');
      if (tdEl) {
        tdEl.setAttribute('data-available', 'false');
        tdEl.appendChild(div);
      }
    });
    return unavailableDates;
  }

  timeToMs(schedule: { startTime: String; endTime: String }) {
    const splitedStartTime = schedule.startTime.split(':');
    const splitedEndTime = schedule.endTime.split(':');
    const initStartHour = Number(splitedStartTime[0]) * 3600000;
    const initStartMin = Number(splitedStartTime[1]) * 60000;
    const endStartHour = Number(splitedEndTime[0]) * 3600000;
    const endStartMin = Number(splitedEndTime[1]) * 60000;
    const initToMs = initStartHour + initStartMin;
    const endTimeToMs = endStartHour + endStartMin;
    return { initToMs, endTimeToMs };
  }
}
